import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { SourceGroup } from '@/components/SourceGroup';
import { ListingCard } from '@/components/ListingCard';
import { EmptyState } from '@/components/EmptyState';
import { SCAN, RESULTS } from '@/constants/testIds';
import { AlertCircle, Package, Layers, LayoutGrid, Loader2, FolderPlus, Save, RefreshCw, Play, ShieldCheck, EyeOff, ChevronDown, ChevronUp, Info, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { SaveToCollectionModal } from '@/components/SaveToCollectionModal';
import {
  sourceStatusLabel, sourceStatusClass, scanStatusLabel,
  isGuidedSource, isResumable, isRerunnable,
} from '@/lib/scanStatus';
import {
  getDeviceId, authenticateWithPasskey, redeemRecoveryCode,
} from '@/lib/guidedScan';

const Results = () => {
  const { scanId } = useParams();
  const [scan, setScan] = useState(null);
  const [groups, setGroups] = useState(null);
  const [combined, setCombined] = useState(null);
  const [mode, setMode] = useState('grouped');
  const [sort, setSort] = useState('opportunity');
  const [poll, setPoll] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [stepUpForSource, setStepUpForSource] = useState(null); // { source_key, action: 'resume'|'rerun' }
  // Aug 2026 §Semantic Qualification — collapsible "Also considered but
  // rejected" panel. Lazily loaded when the user expands it so no wasted
  // bandwidth on the common case.
  const [rejectionsOpen, setRejectionsOpen] = useState(false);
  const [rejections, setRejections] = useState(null);
  const [rejectionsLoading, setRejectionsLoading] = useState(false);

  const runSourceAction = async (sourceKey, action) => {
    const endpoint = action === 'resume' ? 'resume-source' : 'rerun-source';
    try {
      await api.post(`/scans/${scanId}/${endpoint}`, { source_key: sourceKey }, {
        headers: { 'X-Zita-Device-Id': getDeviceId() },
      });
      toast.success(`${action === 'resume' ? 'Resuming' : 'Rerunning'} ${sourceKey.replace(/_/g, ' ')}…`);
      setPoll(true);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 && isGuidedSource(sourceKey)) {
        // Requires Guided Scan step-up
        setStepUpForSource({ source_key: sourceKey, action });
        return;
      }
      toast.error(e.response?.data?.detail || `Could not ${action} source.`);
    }
  };

  const completeStepUp = async () => {
    if (!stepUpForSource) return;
    try {
      await authenticateWithPasskey();
      const { source_key, action } = stepUpForSource;
      setStepUpForSource(null);
      await runSourceAction(source_key, action);
    } catch (e) {
      toast.error('Passkey verification cancelled. Use a recovery code if you cannot verify.');
    }
  };

  const completeStepUpWithRecovery = async (code) => {
    if (!stepUpForSource || !code) return;
    try {
      await redeemRecoveryCode(code);
      const { source_key, action } = stepUpForSource;
      setStepUpForSource(null);
      await runSourceAction(source_key, action);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery code was rejected.');
    }
  };

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const [{ data: s }, { data: g }, { data: c }] = await Promise.all([
          api.get(`/scans/${scanId}`),
          api.get(`/scans/${scanId}/grouped`),
          api.get(`/scans/${scanId}/listings?sort=${sort}`),
        ]);
        if (stop) return;
        setScan(s);
        setGroups(g.groups);
        setCombined(c.listings);
        if ([
          'complete', 'completed', 'completed_with_source_issues',
          'partially_completed', 'partial', 'failed', 'cancelled',
        ].includes(s.status)) {
          setPoll(false);
        }
      } catch (e) { /* keep polling */ }
    };
    load();
    if (!poll) return;
    const id = setInterval(load, 2500);
    return () => { stop = true; clearInterval(id); };
  }, [scanId, sort, poll]);

  if (!scan) return <div className="text-zita-mocha">Loading scan…</div>;

  // Aug 2026 §Honest Counts — headline uses the QUALIFIED count. Raw and
  // rejected are shown as a secondary honesty line so the value of Zita's
  // attention-reduction work is visible without polluting the primary
  // result set. When the qualification stage hasn't run (older scans,
  // legacy consumers, etc.) fall back to raw counts so nothing regresses.
  const qualified = (typeof scan.qualified_total === 'number') ? scan.qualified_total : (combined?.length ?? 0);
  const rawDedup = (typeof scan.raw_total_listings === 'number') ? scan.raw_total_listings : (scan.total_listings ?? qualified);
  const rejectedCount = (typeof scan.rejected_total_listings === 'number') ? scan.rejected_total_listings : Math.max(0, rawDedup - qualified);
  const total = qualified;   // legacy variable name — remains "displayed opportunity count"
  const progress = scan.source_progress || [];
  const searchTerm = scan.command_text || scan.filters?.item_query || '';

  const openRejections = async () => {
    setRejectionsOpen((v) => !v);
    if (rejections !== null || rejectionsLoading) return;
    setRejectionsLoading(true);
    try {
      const { data } = await api.get(`/scans/${scanId}/rejections`);
      setRejections(data);
    } catch (e) {
      toast.error('Could not load hidden results');
    } finally {
      setRejectionsLoading(false);
    }
  };

  const saveScanSnapshot = async () => {
    try {
      const { data } = await api.post('/collections/saved-scans', { scan_id: scanId });
      toast.success(`Scan snapshot saved · ${data.total_listings} items frozen`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error('Ari Collections add-on ($9.99/mo) required for saved scans.');
      } else {
        toast.error(e.response?.data?.detail || 'Could not save scan.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <header className="zita-card p-6 space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="font-serif text-3xl text-zita-espresso">Scan results</h1>
          <span className="text-sm text-zita-muted">
            {scan.command_text || '(extension push)'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-zita-mocha">
          <span>Status: <span className="font-semibold text-zita-espresso">{scanStatusLabel(scan.status)}</span></span>
          <span data-testid="results-qualified-count">
            · <span className="font-semibold text-zita-espresso">{qualified}</span> qualified
            opportunit{qualified !== 1 ? 'ies' : 'y'}
          </span>
          {rejectedCount > 0 && (
            <span data-testid="results-attention-reduction" className="text-zita-muted">
              · Zita reviewed {rawDedup} candidate{rawDedup !== 1 ? 's' : ''} and hid {rejectedCount} as wrong-side or irrelevant
            </span>
          )}
          {scan.duplicates > 0 && <span>· {scan.duplicates} duplicates</span>}
          {scan.failures > 0 && <span>· {scan.failures} source issue{scan.failures !== 1 ? 's' : ''}</span>}
          {scan.scan_kind === 'guided' && (
            <span className="inline-flex items-center gap-1 ari-chip px-2 py-0.5 rounded-full text-[11px]">
              <ShieldCheck size={11} /> Guided Scan
            </span>
          )}
        </div>

        {/* Iteration 37 §Advanced Research Layer — small transparency line
            so the user can see WHAT Ari did beyond the first-pass search. */}
        {scan.research_audit && scan.research_audit.enabled !== false && (
          <div
            className="text-xs text-zita-muted mt-1"
            data-testid="results-research-audit"
          >
            Advanced Research:
            {' '}{scan.research_audit.budget_used?.rounds ?? 0} round{(scan.research_audit.budget_used?.rounds ?? 0) !== 1 ? 's' : ''},
            {' '}{(scan.research_audit.page_inspections?.length) ?? 0} page inspection{(scan.research_audit.page_inspections?.length ?? 0) !== 1 ? 's' : ''},
            {' '}{(scan.research_audit.reformulations?.length) ?? 0} query reformulation{(scan.research_audit.reformulations?.length ?? 0) !== 1 ? 's' : ''},
            {(scan.research_audit.verdicts_upgraded ?? 0) > 0 && <> {' · '}{scan.research_audit.verdicts_upgraded} upgraded to match</>}
            {(scan.research_audit.verdicts_downgraded ?? 0) > 0 && <> {' · '}{scan.research_audit.verdicts_downgraded} downgraded</>}
            {scan.research_audit.stopped_reason && <> {' · '}stopped: {scan.research_audit.stopped_reason.toLowerCase()}</>}
          </div>
        )}

        {/* Negative-result explanation — only when qualified=0 and the
            research layer produced a diagnostic message. Replaces the
            generic "no results" message. */}
        {qualified === 0 && scan.research_negative_reason && (
          <div
            className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900"
            data-testid="results-negative-reason"
          >
            <div className="flex items-start gap-2">
              <Info size={14} className="mt-0.5 shrink-0" />
              <div>{scan.research_negative_reason}</div>
            </div>
          </div>
        )}

        {/* Aug 2026 §19 — Proactive Enhancement Suggestions.
            Ari SPEAKS conversationally (not chips-first). Chips below are
            optional shortcuts. */}
        {scan.enhancement_proposal?.conversational_intro && (
          <EnhancementPanel
            scanId={scanId}
            proposal={scan.enhancement_proposal}
            onApplied={() => window.location.reload()}
          />
        )}

        {/* Aug 2026 §18 — Working Dataset refinement input. Preserves ONE
            visible search thread. Prior state is snapshotted internally. */}
        <RefineBar scanId={scanId} onApplied={() => window.location.reload()} />

        {scan.last_refinement?.explain && (
          <div
            data-testid="results-last-refinement"
            className="text-xs text-zita-mocha bg-zita-sand/60 border border-zita-taupe/60 rounded-lg px-3 py-2"
          >
            <span className="font-semibold">Last refinement:</span> {scan.last_refinement.explain}
          </div>
        )}

        {/* Aug 2026 §6 Loop Scan — direct activation from Seek/Results.
            Auto-saves the parent scan; no separate "save search" step. */}
        {['completed', 'complete', 'partial', 'partially_completed', 'completed_with_source_issues'].includes(scan.status) && !scan.schedule_id && (
          <LoopScanTrigger scan={scan} scanId={scanId} />
        )}
        {scan.schedule_id && (
          <div
            data-testid="results-loop-scan-active"
            className="text-xs inline-flex items-center gap-2 rounded-full bg-ari/10 border border-ari/30 text-ari-accent px-3 py-1.5"
          >
            <RefreshCw size={12} /> Loop Scan active — Ari is watching this search
          </div>
        )}
        <div data-testid={SCAN.progressList} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {progress.map((p) => (
            <div key={p.source_key} data-testid={SCAN.sourceRow} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-zita-taupe bg-white/40 text-sm">
              <div className="flex-1 min-w-0">
                <div className="text-zita-espresso flex items-center gap-1.5">
                  <span className="truncate">{p.display_name}</span>
                  {isGuidedSource(p.source_key) && (
                    <ShieldCheck size={11} className="ari-accent shrink-0" title="Guided Scan source" />
                  )}
                </div>
                {p.message && (
                  <div className="text-[10px] text-zita-muted mt-0.5 truncate">{p.message}</div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <StatusBadge status={p.status} count={p.listings_found} />
                {isResumable(p.status) && (
                  <button
                    type="button"
                    onClick={() => runSourceAction(p.source_key, 'resume')}
                    data-testid={`resume-source-${p.source_key}`}
                    className="p-1 rounded-full hover:bg-zita-sand text-zita-cocoa"
                    aria-label="Resume source"
                    title="Resume Source"
                  >
                    <Play size={12} />
                  </button>
                )}
                {isRerunnable(p.status) && !isResumable(p.status) && (
                  <button
                    type="button"
                    onClick={() => runSourceAction(p.source_key, 'rerun')}
                    data-testid={`rerun-source-${p.source_key}`}
                    className="p-1 rounded-full hover:bg-zita-sand text-zita-cocoa"
                    aria-label="Rerun source"
                    title="Rerun Source"
                  >
                    <RefreshCw size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {total > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              onClick={() => setSavingAll(true)}
              data-testid="results-save-all"
              className="zita-btn-primary text-sm inline-flex items-center gap-1"
            >
              <FolderPlus size={13} /> Save all {total} matches
            </button>
            <button
              type="button"
              onClick={saveScanSnapshot}
              data-testid="results-save-scan"
              className="zita-btn-secondary text-sm inline-flex items-center gap-1"
              title="Freeze this scan as a saved snapshot"
            >
              <Save size={13} /> Save this scan
            </button>
          </div>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setMode('grouped')}
          data-testid={RESULTS.groupBySource}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm ${
            mode === 'grouped' ? 'bg-zita-cocoa text-zita-cream' : 'zita-btn-secondary'
          }`}
        >
          <Layers size={14} /> Grouped by source
        </button>
        <button
          type="button"
          onClick={() => setMode('combined')}
          data-testid={RESULTS.viewCombined}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm ${
            mode === 'combined' ? 'bg-zita-cocoa text-zita-cream' : 'zita-btn-secondary'
          }`}
        >
          <LayoutGrid size={14} /> Combined ranked
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid={RESULTS.sortSelect}
          className="ml-auto zita-input px-3 py-2 text-sm"
        >
          <option value="opportunity">Best opportunity</option>
          <option value="price">Lowest price</option>
          <option value="distance">Closest</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      {total === 0 && scan.status === 'running' && (
        <EmptyState icon={Loader2} title="Scanning…" body="Ari is scanning every active source. Results will appear as they arrive." />
      )}
      {total === 0 && ['completed', 'complete', 'partial', 'partially_completed', 'completed_with_source_issues'].includes(scan.status) && (
        <EmptyState icon={Package} title="No matches this time" body="Adjust your filters, expand your radius, or try a different phrasing." />
      )}

      {/* Aug 2026 §5 — Intelligent Result Clustering / Result Pockets.
          Ari organizes qualified results when volume/diversity makes it
          useful. Users can always see the flat list below. */}
      {scan.result_pockets && scan.result_pockets.length > 0 && (
        <ResultPockets pockets={scan.result_pockets} combined={combined || []} />
      )}

      {mode === 'grouped' && groups && groups.length > 0 && (
        <div className="space-y-4">
          {groups.map((g) => <SourceGroup key={g.source_key} group={g} />)}
        </div>
      )}
      {mode === 'combined' && combined && combined.length > 0 && (
        <div className="space-y-4">
          {combined.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      )}

      {/* Aug 2026 §Semantic Qualification — collapsible rejection audit.
          Renders ONLY when there are hidden results, and always after the
          primary opportunity list so nothing crowds the qualified set. */}
      {rejectedCount > 0 && (
        <div className="zita-card" data-testid="results-rejections-panel">
          <button
            type="button"
            onClick={openRejections}
            className="w-full flex items-center justify-between px-5 py-3 text-left text-sm text-zita-mocha hover:bg-zita-sand/40 rounded-lg"
            data-testid="results-rejections-toggle"
            aria-expanded={rejectionsOpen}
          >
            <span className="inline-flex items-center gap-2">
              <EyeOff size={14} />
              Also considered but rejected ({rejectedCount})
              <span className="text-xs text-zita-muted">
                — click to see what Zita hid and why
              </span>
            </span>
            {rejectionsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {rejectionsOpen && (
            <div className="px-5 pb-5 space-y-3" data-testid="results-rejections-list">
              {rejectionsLoading && (
                <div className="flex items-center gap-2 text-sm text-zita-mocha">
                  <Loader2 size={14} className="animate-spin" /> Loading hidden results…
                </div>
              )}
              {!rejectionsLoading && rejections && rejections.rejections.length === 0 && (
                <div className="text-sm text-zita-muted">No hidden results to show.</div>
              )}
              {!rejectionsLoading && rejections && rejections.rejections.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-2 text-xs" data-testid="rejections-breakdown">
                    {Object.entries(rejections.breakdown || {})
                      .filter(([, n]) => n > 0)
                      .map(([verdict, n]) => (
                      <span
                        key={verdict}
                        className="px-2 py-0.5 rounded-full bg-zita-sand text-zita-cocoa border border-zita-taupe"
                      >
                        {n} {verdict.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                  <ul className="divide-y divide-zita-taupe/40">
                    {rejections.rejections.map((r) => (
                      <li key={r.id} className="py-3 flex flex-col gap-1" data-testid="rejection-row">
                        <div className="flex items-start justify-between gap-3">
                          <a
                            href={r.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-zita-espresso hover:underline flex-1 min-w-0 truncate"
                          >
                            {r.title || r.external_url}
                          </a>
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${
                              r.qualification_verdict === 'wrong_side' ? 'bg-red-50 text-red-700 border border-red-200' :
                              r.qualification_verdict === 'irrelevant' ? 'bg-zinc-100 text-zinc-700 border border-zinc-200' :
                              'bg-amber-50 text-amber-800 border border-amber-200'
                            }`}
                          >
                            {r.qualification_verdict?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="text-xs text-zita-muted flex items-start gap-1">
                          <Info size={11} className="mt-0.5 shrink-0" />
                          <span>{r.qualification_reason || 'No reason recorded.'}</span>
                        </div>
                        <div className="text-[11px] text-zita-muted">
                          {r.discovery_provider || r.source_key}
                          {r.discovery_engine ? ` / ${r.discovery_engine}` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {savingAll && combined && combined.length > 0 && (
        <SaveToCollectionModal
          listings={combined.map((l) => ({ ...l, scan_id: scanId }))}
          searchTerm={searchTerm}
          scanId={scanId}
          onClose={() => setSavingAll(false)}
          onSaved={() => setSavingAll(false)}
        />
      )}
      <StepUpDialog
        stepUp={stepUpForSource}
        onPasskey={completeStepUp}
        onRecovery={completeStepUpWithRecovery}
        onCancel={() => setStepUpForSource(null)}
      />
    </div>
  );
};

const StatusBadge = ({ status, count }) => {
  const isCompleted = status === 'completed' || status === 'complete';
  const text = isCompleted ? `${count || 0} found` : sourceStatusLabel(status);
  return (
    <span
      data-testid={SCAN.statusBadge}
      className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${sourceStatusClass(status)}`}
    >
      {text}
    </span>
  );
};

const StepUpDialog = ({ stepUp, onPasskey, onRecovery, onCancel }) => {
  const [tab, setTab] = useState('passkey');
  const [code, setCode] = useState('');
  if (!stepUp) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" data-testid="guided-step-up-dialog">
      <div className="zita-card p-6 max-w-md w-full space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="ari-accent" />
          <h2 className="font-serif text-xl text-zita-espresso">Verify to continue</h2>
        </div>
        <p className="text-sm text-zita-mocha">
          Guided Scan sources require a device-bound step-up verification before Zita will
          resume them. Verify with your passkey, or use a recovery code.
        </p>
        <div className="flex gap-2 text-sm">
          <button type="button"
            onClick={() => setTab('passkey')}
            className={`px-3 py-1.5 rounded-full ${tab === 'passkey' ? 'bg-zita-cocoa text-zita-cream' : 'zita-btn-secondary'}`}
            data-testid="step-up-tab-passkey"
          >Passkey</button>
          <button type="button"
            onClick={() => setTab('recovery')}
            className={`px-3 py-1.5 rounded-full ${tab === 'recovery' ? 'bg-zita-cocoa text-zita-cream' : 'zita-btn-secondary'}`}
            data-testid="step-up-tab-recovery"
          >Recovery code</button>
        </div>
        {tab === 'passkey' ? (
          <button type="button"
            onClick={onPasskey}
            className="zita-btn-primary text-sm w-full"
            data-testid="step-up-passkey-btn"
          >Verify with passkey</button>
        ) : (
          <div className="space-y-2">
            <input
              type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="zita-input w-full font-mono tracking-widest text-center"
              placeholder="XXXXX-XXXXX" maxLength={11}
              data-testid="step-up-recovery-input"
            />
            <button type="button"
              onClick={() => onRecovery(code)}
              disabled={code.length < 5}
              className="zita-btn-primary text-sm w-full disabled:opacity-50"
              data-testid="step-up-recovery-btn"
            >Redeem recovery code</button>
          </div>
        )}
        <button type="button"
          onClick={onCancel}
          className="zita-btn-secondary text-sm w-full"
          data-testid="step-up-cancel-btn"
        >Cancel</button>
      </div>
    </div>
  );
};

// ============================================================================
// Aug 2026 §6 — Loop Scan activation (auto-saves + starts schedule in one step)
// ============================================================================

const LoopScanTrigger = ({ scan, scanId }) => {
  const [open, setOpen] = useState(false);
  const [cadence, setCadence] = useState('daily');
  const [hour, setHour] = useState(9);
  const [durationKind, setDurationKind] = useState('until_stopped');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = {
        name: (scan.command_text || 'Loop Scan').slice(0, 80),
        command_text: scan.command_text || 'Loop Scan',
        cadence,
        run_times: [{ hour, minute: 0 }],
        auto_save_scan_id: scanId,
      };
      if (durationKind === 'two_weeks') {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        body.end_date = d.toISOString().slice(0, 10);
      } else if (durationKind === 'selected' && endDate) {
        body.end_date = endDate;
      }
      await api.post('/schedules', body);
      toast.success('Loop Scan active — I\'ll keep watching this search.');
      setOpen(false);
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start Loop Scan');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="loop-scan-open"
          className="zita-btn-primary text-sm inline-flex items-center gap-2"
        >
          <RefreshCw size={14} /> Loop Scan this search
        </button>
        <button
          type="button"
          onClick={() => setExplainerOpen(true)}
          data-testid="loop-scan-explain"
          className="text-xs text-zita-mocha hover:text-zita-espresso hover:underline"
        >
          What is a Loop Scan?
        </button>
      </div>

      {explainerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setExplainerOpen(false)}>
          <div className="zita-card p-6 max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-zita-cocoa" />
              <h3 className="font-serif text-lg text-zita-espresso">What is a Loop Scan?</h3>
            </div>
            <p className="text-sm text-zita-mocha leading-relaxed">
              A Loop Scan automatically reruns this search on the schedule you choose and brings you new or meaningful changes — without you having to search again.
            </p>
            <p className="text-sm text-zita-mocha leading-relaxed">
              I'll save the search to your Vault, watch it in the background, and alert you when something new turns up. You can pause, resume, or stop the loop anytime from Vault.
            </p>
            <button
              type="button"
              onClick={() => setExplainerOpen(false)}
              data-testid="loop-scan-explain-close"
              className="zita-btn-secondary text-sm"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="zita-card p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <RefreshCw size={16} className="text-zita-cocoa" />
              <h3 className="font-serif text-lg text-zita-espresso">Loop this search</h3>
            </div>
            <div className="text-xs text-zita-mocha">
              Ari will re-run <span className="font-semibold">{scan.command_text}</span> and save it to your Vault.
            </div>

            <label className="block text-sm">
              <span className="text-zita-espresso font-medium">How often</span>
              <select
                value={cadence}
                onChange={(e) => setCadence(e.target.value)}
                data-testid="loop-scan-cadence"
                className="zita-input w-full mt-1"
              >
                <option value="daily">Every day</option>
                <option value="twice_daily">Twice a day</option>
                <option value="thrice_daily">Three times a day</option>
                <option value="weekdays">Weekdays only</option>
                <option value="weekly">Once a week</option>
                <option value="monthly">Once a month</option>
              </select>
            </label>

            <label className="block text-sm">
              <span className="text-zita-espresso font-medium">At what time</span>
              <select
                value={hour}
                onChange={(e) => setHour(parseInt(e.target.value, 10))}
                data-testid="loop-scan-hour"
                className="zita-input w-full mt-1"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="text-zita-espresso font-medium">Run until</span>
              <select
                value={durationKind}
                onChange={(e) => setDurationKind(e.target.value)}
                data-testid="loop-scan-duration"
                className="zita-input w-full mt-1"
              >
                <option value="until_stopped">Until I stop it</option>
                <option value="two_weeks">Two weeks from today</option>
                <option value="selected">A specific date</option>
              </select>
              {durationKind === 'selected' && (
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="loop-scan-end-date"
                  className="zita-input w-full mt-2"
                />
              )}
            </label>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="zita-btn-secondary text-sm flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy || (durationKind === 'selected' && !endDate)}
                data-testid="loop-scan-submit"
                className="zita-btn-primary text-sm flex-1 inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={14} className="animate-spin" />} Start Loop
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Results;
// ============================================================================

const RefineBar = ({ scanId, onApplied }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    const instruction = text.trim();
    if (!instruction || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/scans/${scanId}/refine`, { instruction });
      const removed = data.removed_count || 0;
      const kept = data.kept_count || 0;
      toast.success(data.explain || `Refined: kept ${kept}, removed ${removed}`);
      setText('');
      onApplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refinement failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-col sm:flex-row gap-2 items-stretch"
      data-testid="results-refine-bar"
    >
      <div className="relative flex-1">
        <Wand2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zita-mocha" />
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
          disabled={busy}
          placeholder='Refine — e.g. "remove anything mentioning engine problems" or "rerank by risk"'
          data-testid="results-refine-input"
          className="zita-input pl-9 pr-3 py-2.5 text-sm w-full"
        />
      </div>
      <button
        type="button"
        onClick={apply}
        disabled={busy || !text.trim()}
        data-testid="results-refine-apply"
        className="zita-btn-primary text-sm px-4 inline-flex items-center gap-2 disabled:opacity-60"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
        Refine
      </button>
    </div>
  );
};

// ============================================================================
// Aug 2026 §19 — Enhancement Search proactive panel (conversational)
// ============================================================================

const EnhancementPanel = ({ scanId, proposal, onApplied }) => {
  const [applying, setApplying] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || !proposal?.conversational_intro) return null;

  const apply = async (enh) => {
    setApplying(enh.id);
    try {
      const { data } = await api.post(`/scans/${scanId}/enhance`, {
        instruction: `${enh.label}: ${enh.why || ''}`.slice(0, 500),
      });
      toast.success(data.explain || `Applied "${enh.label}"`);
      onApplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply');
    } finally {
      setApplying(null);
    }
  };

  return (
    <div
      className="rounded-2xl border border-ari/30 bg-ari/5 p-4 space-y-3"
      style={{ backgroundColor: 'hsla(276, 60%, 55%, 0.06)' }}
      data-testid="results-enhancement-panel"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-ari flex items-center justify-center text-white shrink-0">
          <Sparkles size={14} />
        </div>
        <div className="flex-1 text-sm text-zita-espresso leading-relaxed" data-testid="results-enhancement-intro">
          {proposal.conversational_intro}
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-xs text-zita-mocha hover:text-zita-espresso"
          data-testid="results-enhancement-dismiss"
        >
          Not now
        </button>
      </div>
      {proposal.enhancements?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {proposal.enhancements.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => apply(e)}
              disabled={applying !== null}
              data-testid={`results-enhancement-${e.id}`}
              title={e.why}
              className="text-xs px-3 py-1.5 rounded-full bg-white border border-ari/30 text-zita-espresso hover:bg-ari/10 disabled:opacity-50 inline-flex items-center gap-1"
            >
              {applying === e.id ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              {e.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Aug 2026 §5 — Result Pockets renderer
// ============================================================================

const ResultPockets = ({ pockets, combined }) => {
  const [openId, setOpenId] = useState(pockets[0]?.id || null);
  const byId = new Map((combined || []).map((l) => [l.id, l]));
  const active = pockets.find((p) => p.id === openId);
  const listings = (active?.listing_ids || [])
    .map((id) => byId.get(id))
    .filter(Boolean);

  return (
    <div className="zita-card p-4 space-y-3" data-testid="results-pockets">
      <div className="flex items-center gap-2 text-sm text-zita-mocha">
        <Layers size={14} /> Ari organized these into {pockets.length} pocket{pockets.length !== 1 ? 's' : ''} —
        pick one to focus, or scroll down for the full list.
      </div>
      <div className="flex flex-wrap gap-2" data-testid="results-pocket-tabs">
        {pockets.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setOpenId(p.id === openId ? null : p.id)}
            data-testid={`results-pocket-${p.id}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              openId === p.id
                ? 'bg-zita-espresso text-white border-zita-espresso'
                : 'bg-white text-zita-espresso border-zita-taupe/60 hover:bg-zita-sand/60'
            }`}
          >
            {p.label} <span className="opacity-70">({p.listing_ids.length})</span>
          </button>
        ))}
      </div>
      {active && listings.length > 0 && (
        <div className="space-y-3 pt-2" data-testid={`results-pocket-${active.id}-list`}>
          {active.note && (
            <div className="text-xs text-zita-mocha italic">{active.note}</div>
          )}
          {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      )}
    </div>
  );
};
